import React, {useEffect, useRef, useState} from "react"
import {
    ButtonGroup,
    PopoverInteractionKind,
    PopoverPosition,
} from "@blueprintjs/core"
import {Popover2, Tooltip2} from "@blueprintjs/popover2"
import {useTranslation} from "react-i18next"
import {useDispatch, useSelector} from "react-redux"
import _ from "lodash"
import ExportOptionsCard from "./ExportOptionsCard"
import {RefDbDataContext} from "../../models/displayDataContext"
import {getContext, getMultiContext, getState} from "../../redux/refdb.selector"
import {
    exportDetectabilitySummaryAction,
    exportDisplayDataAction,
    removeFromMultipleContext,
    setDisplayDataContextAction,
    setMultipleContextAction,
} from "../../redux/refdb.actions"
import MinimalButton from "../../../core/components/buttons/MinimalButton"
import ActionButton from "../../../core/components/buttons/ActionButton"
import {Subset} from "../../models/subset"
import FilterOptionPanelInternal from "./FilterOptionPanelInternal"
import BasicsSelectPanel from "./BasicsSelectPanel"
import GenerateTableCard from "./GenerateTableCard"
import GenerateTableType from "../../../shared/enums/generateTableType"
import DisplayOptionsCardInternal from "./DisplayOptionsCardInternal"
import {Switches} from "../../models/componentSwitch"
import {showToast} from "../../../common/toaster/AppToasterWrapper"
import Filter from "../../models/filter"

export default function DisplayDataConfigBarInternal() {
    const {t} = useTranslation()
    const dispatch = useDispatch()

    const contextData = useSelector(getContext)
    const multiContext = useSelector(getMultiContext)

    const state = useSelector(getState)

    const [subsets, setSubsets] = useState<Subset[]>([new Subset()])

    const [context, setContext] = useState<RefDbDataContext>(contextData)

    const isContextDirty = useRef(false)
    /**
     * Write changes in local context to redux state
     */
    useEffect(() => {
        console.log("Dispatch context to redux...")
        dispatch(setDisplayDataContextAction(context))
        isContextDirty.current = true
    }, [context])

    const exportDisplayData = () => {
        if (_.isUndefined(contextData.exportFileType)) {
            showToast({
                type: "info",
                message: "File type not selected.",
            })
        } else if (
            state.generateTable === GenerateTableType.detectabilitySummary
        ) {
            dispatch(exportDetectabilitySummaryAction())
        } else {
            dispatch(exportDisplayDataAction("Internal"))
        }
    }

    const handleAddClick = () => {
        setSubsets([...subsets, new Subset()])
        const index = subsets.length
        if (index > 0) {
            const newContext = _.cloneDeep(multiContext[0])
            newContext.index = index
            dispatch(setMultipleContextAction(newContext))
        } else {
            dispatch(
                setMultipleContextAction(
                    RefDbDataContext.of(
                        subsets.length,
                        [],
                        Object.values(Switches)
                    )
                )
            )
        }
    }

    // handle click event of the Remove button
    const handleRemoveClick = (index: number) => {
        const list = [...subsets]
        list.splice(index, 1)
        setSubsets(list)
        dispatch(removeFromMultipleContext(index))
    }
    const [isOpen, setOpen] = useState<boolean[]>([])

    const changeState = (i: number) => {
        const list = [...isOpen]
        list[i] = list[i] ? !list[i] : true
        setOpen(list)
    }

    const mandatoryFieldsEmpty = (i: number) => {
        return (
            Filter.isEmptyValues(multiContext[i].assay.value) ||
            Filter.isEmptyValues(multiContext[i].material) ||
            Filter.isEmptyValues(multiContext[i].species)
        )
    }

    return (
        <div style={{display: "flex"}}>
            <div className="DisplayDataConfigBarContainer">
                {subsets.map((x, i) => {
                    return (
                        <div>
                            <div className="DisplayDataConfigBar">
                                <Tooltip2
                                    content={t("Select your basics")}
                                    position={PopoverPosition.BOTTOM}
                                >
                                    <Popover2
                                        className="DisplayDataConfigBarElement"
                                        interactionKind={
                                            PopoverInteractionKind.CLICK
                                        }
                                        position={PopoverPosition.BOTTOM_LEFT}
                                        modifiers={{arrow: {enabled: true}}}
                                        content={
                                            <BasicsSelectPanel
                                                subset={i}
                                                className="DisplayDataConfigBarElement"
                                            />
                                        }
                                    >
                                        <MinimalButton
                                            text="Select your basics"
                                            rightIcon="caret-down"
                                        />
                                    </Popover2>
                                </Tooltip2>

                                <MinimalButton
                                    text="Filter options"
                                    rightIcon="caret-down"
                                    onClick={() => changeState(i)}
                                    disabled={mandatoryFieldsEmpty(i)}
                                />

                                <Tooltip2
                                    content={t("Set display options")}
                                    position={PopoverPosition.BOTTOM}
                                >
                                    <Popover2
                                        className="DisplayDataConfigBarElement"
                                        interactionKind={
                                            PopoverInteractionKind.CLICK
                                        }
                                        position={PopoverPosition.BOTTOM_LEFT}
                                        modifiers={{arrow: {enabled: true}}}
                                        disabled={mandatoryFieldsEmpty(i)}
                                        content={
                                            <DisplayOptionsCardInternal
                                                subset={i}
                                            />
                                        }
                                    >
                                        <MinimalButton
                                            text="Display options"
                                            icon="eye-open"
                                            rightIcon="caret-down"
                                            disabled={mandatoryFieldsEmpty(i)}
                                        />
                                    </Popover2>
                                </Tooltip2>
                                <MinimalButton
                                    disabled={subsets.length === 1}
                                    text="Remove subset"
                                    onClick={() => handleRemoveClick(i)}
                                />
                            </div>
                            <FilterOptionPanelInternal
                                open={isOpen[i]}
                                subset={i}
                            />
                        </div>
                    )
                })}
                <div>
                    {subsets.length < 10 && (
                        <MinimalButton
                            text="Add subset"
                            onClick={handleAddClick}
                        />
                    )}
                </div>
            </div>
            <div style={{marginTop: "10px"}}>
                <GenerateTableCard />
                <ButtonGroup className="DisplayDataConfigBarElement">
                    <Tooltip2
                        content={t("Export data as file")}
                        position={PopoverPosition.BOTTOM}
                    >
                        <ActionButton
                            text="Export"
                            icon="download"
                            onClick={exportDisplayData}
                            disabled={_.isUndefined(state.generateTable)}
                        />
                    </Tooltip2>
                    <Tooltip2
                        content={t("Set export options")}
                        position={PopoverPosition.BOTTOM}
                    >
                        <Popover2
                            interactionKind={PopoverInteractionKind.CLICK}
                            position={PopoverPosition.BOTTOM_RIGHT}
                            modifiers={{
                                arrow: {enabled: true},
                            }}
                            disabled={_.isUndefined(state.generateTable)}
                            content={
                                <ExportOptionsCard
                                    exportDisplayData={exportDisplayData}
                                />
                            }
                        >
                            <ActionButton
                                rightIcon="caret-down"
                                disabled={_.isUndefined(state.generateTable)}
                            />
                        </Popover2>
                    </Tooltip2>
                </ButtonGroup>
            </div>
        </div>
    )
}
